import React, { createContext, useState, useEffect } from 'react';
import { User, signIn, signUp, logOut, onAuthStateChanged, db } from '@municipality/firebase';
import { normalizeRole, isSuperAdmin as isSuperAdminRole, SuperAdminRole } from '@municipality/types';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';

// Extend the User type to include super admin properties
interface ExtendedUser extends Omit<User, 'phoneNumber'> {
  role?: string;
  lastLogin?: string;
  superAdminRole?: SuperAdminRole;
  name?: string;
  surname?: string;
  phoneNumber?: string | null | undefined;
  municipalityAssignments?: Array<{ municipalityId: string; expiresAt?: Date | { toDate: () => Date } }>;
  assignedMunicipalityIds?: string[];
}

interface AuthContextType {
  user: ExtendedUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  logOut: () => Promise<void>;
  isSuperAdmin: boolean;
}

export type { AuthContextType };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

/** Throttle lastLogin Firestore writes - only update once per 5 min per user */
const LAST_LOGIN_THROTTLE_MS = 5 * 60 * 1000;
const lastLoginUpdatedAt = new Map<string, number>();

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  console.log('🔐 AuthContext: Current location:', location.pathname);
  console.log('🔐 AuthContext: Initializing:', initializing);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (user) => {
      console.log('🔐 AuthContext: Auth state changed, user:', user?.uid);
      
      if (user) {
        try {
          // Retry getDoc - user doc may not exist yet during activation (acceptInvitation creates it)
          let userDoc = await getDoc(doc(db, 'users', user.uid));
          let userData = userDoc.data();
          if (!userData && location.pathname === '/activate') {
            for (let attempt = 0; attempt < 5; attempt++) {
              await new Promise((r) => globalThis.setTimeout(r, 500));
              userDoc = await getDoc(doc(db, 'users', user.uid));
              userData = userDoc.data();
              if (userData) break;
            }
          }
          const role = userData?.role;
          const canonicalRole = normalizeRole(role || null);
          
          // Update lastLogin timestamp in Firestore (throttled to once per 5 min per user)
          const now = Date.now();
          const lastUpdated = lastLoginUpdatedAt.get(user.uid) ?? 0;
          if (now - lastUpdated >= LAST_LOGIN_THROTTLE_MS) {
            try {
              await updateDoc(doc(db, 'users', user.uid), {
                lastLogin: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              });
              lastLoginUpdatedAt.set(user.uid, now);
              console.log('✅ AuthContext: Updated lastLogin timestamp in Firestore');
            } catch (updateError) {
              console.warn('⚠️ AuthContext: Failed to update lastLogin timestamp:', updateError);
            }
          } else {
            console.log('🔐 AuthContext: Skipping lastLogin update (throttled)');
          }
          
          // Include super admin properties
          // Default superAdminRole to SUPER_ADMIN if user has super_admin role but no superAdminRole field
          let superAdminRoleValue = userData?.superAdminRole as SuperAdminRole | undefined;
          if (!superAdminRoleValue && (canonicalRole === 'super_admin' || role === 'super_admin')) {
            console.log('⚠️ AuthContext: User has super_admin role but no superAdminRole field, defaulting to SUPER_ADMIN');
            superAdminRoleValue = SuperAdminRole.SUPER_ADMIN;
            
            // Update Firestore with default superAdminRole
            try {
              await updateDoc(doc(db, 'users', user.uid), {
                superAdminRole: SuperAdminRole.SUPER_ADMIN,
                updatedAt: new Date().toISOString()
              });
              console.log('✅ AuthContext: Updated user with default superAdminRole');
            } catch (updateError) {
              console.warn('⚠️ AuthContext: Failed to update superAdminRole:', updateError);
            }
          }
          
          const extendedUser: ExtendedUser = { 
            ...user, 
            role: canonicalRole || role, 
            lastLogin: new Date().toISOString(),
            superAdminRole: superAdminRoleValue,
            name: userData?.name,
            surname: userData?.surname,
            phoneNumber: userData?.phoneNumber,
            municipalityAssignments: userData?.municipalityAssignments as ExtendedUser['municipalityAssignments'],
            assignedMunicipalityIds: userData?.assignedMunicipalityIds as string[] | undefined,
          };
          
          console.log('🔐 AuthContext: User role:', role, 'canonical:', canonicalRole, 'superAdminRole:', superAdminRoleValue);
          setUser(extendedUser);
          setLoading(false);

          if (isSuperAdminRole(canonicalRole)) {
            // Only redirect to dashboard when user just logged in from /login; never on reload
            if (location.pathname === '/login') {
              console.log('🔐 AuthContext: Redirecting to dashboard (just logged in)');
              navigate('/dashboard', { replace: true });
            } else {
              console.log('🔐 AuthContext: User authenticated, staying on current page:', location.pathname);
            }
          } else {
            console.log('🔐 AuthContext: User is not super-admin, redirecting to unauthorized');
            await logOut();
            navigate('/unauthorized');
          }
        } catch (error) {
          console.error('🔐 AuthContext: Error fetching user role:', error);
          setUser(null);
          setLoading(false);
          navigate('/unauthorized');
        }
      } else {
        console.log('🔐 AuthContext: No user, clearing state');
        setUser(null);
        setLoading(false);
      }

      if (initializing) {
        console.log('🔐 AuthContext: Setting initializing to false');
        setInitializing(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, location.pathname, initializing]);

  const handleSignIn = async (email: string, password: string) => {
    try {
      console.log('🔐 AuthContext: Signing in user:', email);
      await signIn(email, password);
    } catch (error) {
      console.error('🔐 AuthContext: Error signing in:', error);
      throw error;
    }
  };

  const handleSignUp = async (email: string, password: string, displayName: string) => {
    try {
      console.log('🔐 AuthContext: Signing up user:', email);
      await signUp(email, password, displayName);
    } catch (error) {
      console.error('🔐 AuthContext: Error signing up:', error);
      throw error;
    }
  };

  const handleLogOut = async () => {
    try {
      console.log('🔐 AuthContext: Logging out user');
      if (user?.uid) lastLoginUpdatedAt.delete(user.uid);
      await logOut();
      navigate('/login');
    } catch (error) {
      console.error('🔐 AuthContext: Error logging out:', error);
      throw error;
    }
  };

  const isSuperAdmin = isSuperAdminRole(user?.role);

  return (
    <AuthContext.Provider value={{ user, loading, signIn: handleSignIn, signUp: handleSignUp, logOut: handleLogOut, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}; 